/* Filter und Sortierung der Kaderliste nach Jahrgang und Alter — reine Funktionen, kein DOM.
 *
 * Nur für die Kaderliste (SquadList.vue). Transferierte, Freigegebene und der Export benutzen diese
 * Datei nicht. Entscheidung Bastian, 2026-10-05: kein eigener Altersfilter — die Jahrgangs-Knöpfe
 * (2008, 2007 …) ersetzen ihn; zusätzlich lässt sich die Liste nach Alter sortieren.
 *
 * Der Jahrgang kommt aus dem Geburtsdatum `dob`, `jg` gilt nur als Ersatz (domain/birthYear.js) —
 * dieselbe Regel wie in der View players_public (supabase/gaeste-jahrgang-aus-dob.sql).
 */

import { positionGroup, POSITION_GROUPS } from './positions.js';
import { birthYearOf, validDob } from './birthYear.js';
import { ageGroupOf, AGE_GROUP_ALL } from './ageGroups.js';

export const ALL = 'all';

function text(v) {
  return v == null ? '' : String(v).trim();
}

/**
 * Sortierschlüssel nach Alter (Text, aufsteigend = ältester zuerst). Mit ISO-Geburtsdatum: das Datum.
 * Nur Jahr bekannt: "JJJJ-00-00", also vor allen genauen Daten desselben Jahrgangs — eine feste,
 * für alle gleiche Einordnung, damit die Reihenfolge stabil und widerspruchsfrei bleibt. Ohne Jahr: ''.
 */
export function birthKeyOf(player) {
  const dob = validDob(player && player.dob);
  if (dob) return dob;
  const year = birthYearOf(player);
  return year ? `${year}-00-00` : '';
}

/** Jahrgänge der Spieler, jüngster zuerst, ohne Doppelte und ohne leere. */
export function squadYears(players) {
  const years = new Set((players || []).map(birthYearOf).filter(Boolean));
  return [...years].sort((a, b) => b.localeCompare(a));
}

/** Ist der gewählte Jahrgang noch da? Sonst fällt der Filter auf "Alle" zurück. */
export function effectiveYear(year, years) {
  return year !== ALL && (years || []).includes(year) ? year : ALL;
}

/**
 * Spieler nach Positionsblock ('st' | 'wing' | 'mid' | 'def' | 'gk' | 'open' | 'all'), Jahrgang ('2008' | 'all')
 * und Altersklasse ('U17' | 'U19' | 'all'). Alle Filter gelten zusammen (UND).
 */
export function filterSquad(players, { position = ALL, year = ALL, ageGroup = AGE_GROUP_ALL } = {}) {
  return (players || []).filter(
    (p) =>
      (position === ALL || positionGroup(p.pos) === position) &&
      (year === ALL || birthYearOf(p) === year) &&
      (ageGroup === AGE_GROUP_ALL || ageGroupOf(p) === ageGroup)
  );
}

/**
 * Zahl der aktiven Filter für den Knopf "Filter · n" (F2): Position nicht "all", Jahrgang nicht "all", Altersklasse nicht
 * "all". Die Sortierung und die Suche zählen NICHT mit (die Suche hat ihr eigenes Feld; die Sortierung filtert nichts).
 * Es gibt keinen "Markiert"-Filter: Der Markier-Modus ist eine Auswahl, kein Filter, und zählt deshalb nicht.
 */
export function activeFilterCount({ position = ALL, year = ALL, ageGroup = AGE_GROUP_ALL } = {}) {
  return [position !== ALL, year !== ALL, ageGroup !== AGE_GROUP_ALL].filter(Boolean).length;
}

/**
 * Anzahl je Positionsblock für die Filterknöpfe — bezogen auf den gewählten Jahrgang, damit eine
 * Zahl dem entspricht, was man nach dem Klick sieht. `players` ist schon von der Suche gefiltert.
 */
export function positionCountsFor(players, year = ALL, ageGroup = AGE_GROUP_ALL) {
  const list = filterSquad(players, { year, ageGroup });
  const counts = { [ALL]: list.length };
  for (const key of POSITION_GROUPS) counts[key] = 0;
  for (const p of list) counts[positionGroup(p.pos)] += 1;
  return counts;
}

/**
 * Anzahl je Jahrgang für die Knöpfe — bezogen auf den gewählten Positionsblock. Jeder Jahrgang aus
 * `years` bekommt einen Eintrag (auch 0). Spieler ohne Jahrgang zählt nur "Alle".
 */
export function yearCountsFor(players, years, position = ALL, ageGroup = AGE_GROUP_ALL) {
  const list = filterSquad(players, { position, ageGroup });
  const counts = { [ALL]: list.length };
  for (const y of years) counts[y] = 0;
  for (const p of list) {
    const y = birthYearOf(p);
    if (y in counts) counts[y] += 1;
  }
  return counts;
}

/* ------------------------------------------------------------ Sortierung */

export const AGE_DIRECTIONS = ['youngest', 'oldest'];
export const DEFAULT_AGE_DIRECTION = 'youngest';

function nameOf(p) {
  return [text(p.lastName), text(p.firstName)];
}
const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

function byName(a, b) {
  const [al, af] = nameOf(a);
  const [bl, bf] = nameOf(b);
  return collator.compare(al, bl) || collator.compare(af, bf);
}

/**
 * Vergleich nach Alter. 'youngest' = jüngster zuerst (späteres Geburtsdatum zuerst), 'oldest' = ältester
 * zuerst. Gleicher Schlüssel (gleiches Datum oder nur Jahr bekannt): Nachname, dann Vorname — in beiden
 * Richtungen gleich. Spieler ohne Geburtsdatum und ohne Jahrgang stehen in beiden Richtungen am Ende.
 */
export function compareByAge(direction = DEFAULT_AGE_DIRECTION) {
  const sign = direction === 'oldest' ? 1 : -1;
  return (a, b) => {
    const ka = birthKeyOf(a);
    const kb = birthKeyOf(b);
    if (!ka && !kb) return byName(a, b);
    if (!ka) return 1;
    if (!kb) return -1;
    return sign * ka.localeCompare(kb) || byName(a, b);
  };
}

/** Spieler nach Alter sortiert; verändert die Eingabe nicht. */
export function sortByAge(players, direction = DEFAULT_AGE_DIRECTION) {
  return (players || []).slice().sort(compareByAge(direction));
}

/**
 * Nach Alter sortiert und je Jahrgang gruppiert (key = '2008'; Spieler ohne Jahrgang ganz am Ende
 * unter key ''). Dieselbe Form wie groupByPosition und groupByLetter (key + members), damit
 * SquadList.vue alle Modi mit derselben Vorlage zeigt. Die Richtung kehrt Gruppen UND Reihenfolge
 * innerhalb der Gruppe um.
 */
export function groupByAge(players, direction = DEFAULT_AGE_DIRECTION) {
  const groups = [];
  for (const p of sortByAge(players, direction)) {
    const key = birthYearOf(p);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.members.push(p);
    else groups.push({ key, members: [p] });
  }
  return groups;
}
